/*!
 * textAlong.js — text(..., along:x): a text running along a shape's outline or a sound's trace
 * © 2026 Rob Canning — GPLv3
 *
 *     text(uid:t, pick:["round and round"], dur:60, along:star)   round the shape star's outline,
 *                                                     turning with it, as it moves
 *     text(uid:t, data:bin, length:200, along:bass)   riding the sound bass's trace, bending with it
 *     text(uid:t, ..., along:star, speed:80)          crawling along it, 80 px a second (- the other way)
 *     text(uid:t, along:off)                          as it was
 *
 * The text is what its own line says -- a cycle:, data: lines on the beat --
 * read from its lines (out of sight while it runs along) each frame, in their
 * font and colour; each character set along the path, turned to follow it,
 * on a canvas over the stage. Round a shape it goes round and round; along a
 * trace it runs from the start, as much as fits. Pure placement (placeAlong)
 * apart.
 */

import { outlinesOf } from "./stageMask.js";
import { waveformsForElement } from "./waveform.js";
import { isDarkStage } from "./darkStage.js";
import { onReset } from "./teardown.js";

const alongs = new Map();            // text uid -> { path: uid, speed, t0 }
let layer = null, raf = 0;

/**
 * Where each character goes: on the polyline `pts` ([x, y] ...), `closed`
 * round, from `offset` px along it, each `widths[i]` px wide. [{ x, y, a }]
 * (a: the angle of the path there), null for one off the end of an open path.
 */
export function placeAlong(pts, closed, widths, offset = 0) {
  const seg = [], acc = [0];
  const n = pts.length, m = closed ? n : n - 1;
  for (let i = 0; i < m; i++) {
    const [x0, y0] = pts[i], [x1, y1] = pts[(i + 1) % n];
    seg.push([x0, y0, x1, y1, Math.hypot(x1 - x0, y1 - y0)]);
    acc.push(acc[acc.length - 1] + seg[seg.length - 1][4]);
  }
  const total = acc[acc.length - 1];
  if (!(total > 0)) return widths.map(() => null);
  const out = [];
  let s = offset;
  for (const w of widths) {
    let at = s + w / 2;
    s += w;
    if (closed) at = ((at % total) + total) % total;
    else if (at < 0 || at > total) { out.push(null); continue; }
    // the segment it falls on
    let k = 0;
    while (k < seg.length - 1 && acc[k + 1] < at) k++;
    const [x0, y0, x1, y1, len] = seg[k];
    const t = len ? (at - acc[k]) / len : 0;
    out.push({ x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t, a: Math.atan2(y1 - y0, x1 - x0) });
  }
  return out;
}

/** The path on the screen: a shape's outline (closed), or a sound's trace (open), or null. */
function pathOf(uid) {
  const outline = outlinesOf(uid)[0];
  if (outline?.length > 2) return { pts: outline, closed: true };
  const box = document.getElementById(`scope-${uid}`) || document.getElementById(uid);
  const h = box ? waveformsForElement(box).find(w => w._lastPts?.length > 3) : null;
  const m = h?.group?.getScreenCTM?.();
  if (!h || !m) return null;
  const p = h._lastPts, pts = [];
  for (let i = 0; i < p.length; i += 2) {
    const q = new DOMPoint(p[i], p[i + 1]).matrixTransform(m);
    pts.push([q.x, q.y]);
  }
  return { pts, closed: false };
}

const textEls = uid => [...document.querySelectorAll(`[data-uid="${CSS.escape(uid)}"]`)].filter(e => !e.closest("svg"));

function frame(now) {
  raf = 0;
  if (!alongs.size) { layer?.remove(); layer = null; return; }
  if (!layer) {
    layer = document.createElement("canvas");
    layer.id = "oscilla-text-along";
    layer.style.cssText = "position:fixed;left:0;top:0;width:100vw;height:100vh;pointer-events:none;z-index:9997";
    document.body.appendChild(layer);
  }
  const dpr = window.devicePixelRatio || 1;
  if (layer.width !== Math.round(innerWidth * dpr) || layer.height !== Math.round(innerHeight * dpr)) {
    layer.width = Math.round(innerWidth * dpr); layer.height = Math.round(innerHeight * dpr);
  }
  // the dark stage inverts the page: turned back, so the letters show in their own colour
  const filter = isDarkStage() ? "invert(1) hue-rotate(180deg)" : "";
  if (layer.style.filter !== filter) layer.style.filter = filter;
  const g = layer.getContext("2d");
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, layer.width, layer.height);
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  for (const [uid, a] of alongs) {
    const els = textEls(uid);
    const path = pathOf(a.path);
    if (!els.length || !path) continue;
    // the text's lines out of sight: they are drawn here, along the path
    for (const el of els) if (el.style.visibility !== "hidden") { el._alongWas = el.style.visibility; el.style.visibility = "hidden"; }
    const cs = getComputedStyle(els[0].querySelector("*") || els[0]);
    const text = els.map(e => e.textContent.trim()).filter(Boolean).join(" ");
    if (!text) continue;
    g.font = cs.font;
    g.fillStyle = cs.color;
    g.textAlign = "center";
    g.textBaseline = "middle";
    const chars = [...text];
    const widths = chars.map(c => g.measureText(c).width || 1);
    const offset = a.speed * (now - a.t0) / 1000;
    const at = placeAlong(path.pts, path.closed, widths, offset);
    chars.forEach((c, i) => {
      const p = at[i];
      if (!p || !c.trim()) return;
      g.save(); g.translate(p.x, p.y); g.rotate(p.a); g.fillText(c, 0, 0); g.restore();
    });
  }
  raf = requestAnimationFrame(frame);
}

/** text `uid` along `path` (a shape's or a sound's uid), crawling `speed` px a second; null / off as it was. */
export function setAlong(uid, path, speed = 0) {
  const off = path == null || /^(off|none|0)$/i.test(String(path));
  if (off) {
    alongs.delete(uid);
    for (const el of textEls(uid)) if (el.style.visibility === "hidden") el.style.visibility = el._alongWas ?? "";
  } else {
    alongs.set(uid, { path: String(path), speed: Number(speed) || 0, t0: performance.now() });
  }
  if (!raf) raf = requestAnimationFrame(frame);
}

/** Every text back as it was (still(all), a new example). */
export function clearAlongs() {
  for (const uid of [...alongs.keys()]) setAlong(uid, null);
}
onReset("stage", clearAlongs);
